import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import {
  Checkbox,
  Col,
  Dropdown,
  Modal,
  Row,
  Space,
  Table,
  Tooltip
} from 'antd'
import styled from 'styled-components'
import produce from 'immer'
import _ from 'lodash'

import { DownOutlined } from '@ant-design/icons'
import { colours, sizes } from '@qflow/theme'

import { convertObjectArrayToString } from 'portal/lib/primitives/ResponsiveTable/common.ts'
import { DateFormatFull } from 'portal/lib/functions'
import {
  useCheckboxStates,
  useModuleIds,
  useUserProjectRole
} from 'portal/lib/hooks'

import {
  DetailRow,
  AttachmentsSection,
  ValueAdd
} from 'portal/components/RecordDetails'
import { AttachmentsHeader } from 'portal/components/RecordDetails/AttachmentsHeader'
import { UpgradeToPro } from 'portal/components/RecordDetails/SharedComponents/UpgradeToPro'

import {
  RemoveRecordButton,
  EditRecordButton,
  LineItemButtons,
  LineItemDetailButton,
  ViewInAdminButton,
  InfractionFlag,
  SelectProductGroup,
  useProductGroupsLoader
} from 'portal/components/RecordsModules/common'

import { convertAssignedToArrayToString, imperial } from 'portal/lib/functions'
import {
  ColourButton,
  Flex,
  icons,
  SmallSpinner,
  SubHeading,
  Spacer,
  ResponsiveTable,
  Text,
  PillBox
} from 'portal/lib/primitives'

import { useInfractionGetter } from 'portal/store/infractions/InfractionsContext'
import { trackMixpanel } from 'portal/store/mixpanel/actions'
import { useRecordEditing } from 'portal/store/records/editing/useRecordEditing'

export function Delivery({ record }) {
  const [indeterminate, setIndeterminate] = useState(false)
  const [checkAll, setCheckAll] = useState(false)
  const [showProductGroupsModal, setShowProductGroupsModal] = useState(false)
  const [selectedProductGroupId, setSelectedProductGroupId] = useState(null)
  const [savingProductGroups, setSavingProductGroups] = useState(false)

  const [
    checkedProductIds,
    setCheckedProductId,
    setBulkCheckedProductId,
    clearCheckedProducts
  ] = useCheckboxStates()

  const { accountId, projectId, moduleName } = useModuleIds()

  const { isReadOnly } = useUserProjectRole(accountId, projectId)

  const {
    loading: productGroupsLoading,
    error: productGroupsError,
    reload: productGroupsReload
  } = useProductGroupsLoader()

  const dispatch = useDispatch()
  const editing = useRecordEditing()
  const getInfraction = useInfractionGetter()

  const projectTimeZone = useSelector(
    state => state.projectDetails.modules?.timeZone
  )

  const projectIsProLicence = useSelector(
    state => state.projectDetails.modules?.isProLicence
  )

  const projectCountry = useSelector(
    state => state.projectDetails.modules?.country
  )

  const allProductGroups = useSelector(state => state.productGroups)

  const isImperial = imperial(projectCountry)

  const allProductIds = useMemo(
    () =>
      record.data?.products?.map(product =>
        !product.isRemoved ? product.id : null
      ),
    [record.data.products]
  )

  const dataForProductsTable = useMemo(() => {
    let products = []
    if (record?.data?.products) {
      const originalProducts = [...record.data.products]
      originalProducts.forEach((element, index) => {
        products.push({ originalIndex: index, ...element })
      })
      products = products.filter(p => !p.isDeleted)
    }
    if (record?.dataSourceTracking?.products) {
      products = products.map((product, index) => {
        const dataSourceProduct = record.dataSourceTracking.products[index]
        if (dataSourceProduct) {
          return {
            ...product,
            valueAdd: dataSourceProduct
          }
        }
        return product
      })
    }
    return products
  }, [record])

  const productTableHeaders = useMemo(() => {
    const columns = [
      {
        key: 'name-ValueAdd',
        dataIndex: 'valueAdd',
        render: item => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd details={{ fieldName: 'name', ...item.name }} />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      { key: 'name', title: 'Name', dataIndex: 'name' },
      {
        key: '__issues__',
        title: 'Issues',
        render: (item, record, index) => {
          const infractions = getInfraction(`data.products[${index}]`, {
            isPrefix: true
          })

          if (!infractions?.length) {
            return null
          }

          return (
            <PillBox>
              {infractions.map((v, i) => (
                <InfractionFlag key={i} />
              ))}
            </PillBox>
          )
        }
      },
      {
        key: 'givenVolume-ValueAdd',
        dataIndex: 'valueAdd',
        render: item => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{ fieldName: 'givenVolume', ...item.givenVolume }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      {
        key: 'givenVolume',
        title: isImperial ? (
          'Volume'
        ) : (
          <>
            Volume{' '}
            <span>
              (m<sup>3</sup>)
            </span>
          </>
        ),
        dataIndex: 'givenVolume',
        render: item => convertObjectArrayToString(item)
      },
      {
        key: 'weight-ValueAdd',
        dataIndex: 'valueAdd',
        render: item => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd details={{ fieldName: 'weight', ...item.weight }} />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      {
        key: 'weight',
        title: isImperial ? (
          'Weight'
        ) : (
          <>
            Weight <span>(t)</span>
          </>
        ),
        dataIndex: 'weight',
        render: item => convertObjectArrayToString(item)
      },
      {
        key: 'quantity-ValueAdd',
        dataIndex: 'valueAdd',
        render: item => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd details={{ fieldName: 'quantity', ...item.quantity }} />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      { key: 'quantity', title: 'Quantity', dataIndex: 'quantity' },
      {
        key: 'productGroups-ValueAdd',
        dataIndex: 'valueAdd',
        render: item => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{ fieldName: 'productGroups', ...item.productGroups }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      {
        key: '__groups__',
        title: 'Product Group',
        render: item => {
          let returnElement = null
          if (
            item.productGroups &&
            item.productGroups[0] &&
            item.isHiredOrRented
          ) {
            // Is an array but we only return the first item
            returnElement = (
              <Text key={item.productGroups[0].productGroupId}>
                {item.productGroups[0].name}, Hired or Rented
              </Text>
            )
          } else if (item.productGroups && item.productGroups[0]) {
            returnElement = (
              <Text key={item.productGroups[0].productGroupId}>
                {item.productGroups[0].name}
              </Text>
            )
          } else if (item.isHiredOrRented) {
            returnElement = <Text>Hired or Rented</Text>
          }

          return returnElement
        }
      },
      {
        key: 'embodiedCarbonKgCO2e-ValueAdd',
        dataIndex: 'valueAdd',
        render: item => {
          if (!item) {
            return null
          }
          return (
            <NegativeMargin>
              <ValueAdd
                details={{
                  fieldName: 'embodiedCarbonKgCO2e',
                  ...item.embodiedCarbonKgCO2e
                }}
              />
            </NegativeMargin>
          )
        },
        width: '1px'
      },
      {
        key: 'embodiedCarbonKgCO2e',
        title: (
          <StyledLabel>
            <div
              style={
                !projectIsProLicence ? { color: colours.BATTLESHIP_GREY } : {}
              }
            >
              Embodied Carbon{' '}
              <span>
                (kg CO<sub>2</sub>e)
              </span>
            </div>
            {!projectIsProLicence && <UpgradeToPro />}
          </StyledLabel>
        ),
        render: item =>
          item.embodiedCarbonKgCO2e
            ? _.round(item.embodiedCarbonKgCO2e, 2)
            : null
      },
      {
        key: 'actions',
        title: '',
        render: (item, product, index) =>
          !item.isDeleted && record.data?.products[item.originalIndex] ? (
            <ProductControls record={record} index={item.originalIndex} />
          ) : (
            `${item.originalIndex}`
          ),
        width: '130px',
        style: { display: 'flex' }
      }
    ]

    // if user is contributor or admin add selection row.
    if (
      record.canEdit &&
      !isReadOnly &&
      record.data.products &&
      record.data.products.length > 0
    ) {
      const selectMenuItems = [
        { label: 'Select all', key: 'selectAll' },
        { label: 'Deselect all', key: 'deselectAll' }
      ]

      const handleSelectMenuClick = e => {
        switch (e.key) {
          // Select all records including ones not currently loaded
          case 'selectAll':
            setCheckAll(true)
            setBulkCheckedProductId(allProductIds, true)
            break
          case 'deselectAll':
            clearCheckedProducts()
            break
        }
      }

      const selectionColumn = {
        title: (
          <Dropdown
            menu={{
              items: selectMenuItems,
              onClick: handleSelectMenuClick
            }}
          >
            <Space>
              <Checkbox
                style={{ pointerEvents: 'none' }}
                indeterminate={indeterminate}
                checked={checkAll}
              ></Checkbox>
              <DownOutlined />
            </Space>
          </Dropdown>
        ),
        dataIndex: 'id',
        className: 'id',
        width: '60px',
        render: (item, product) => (
          <Checkbox
            checked={checkedProductIds.byKey[item]}
            onChange={e => {
              setCheckedProductId(item, e.target.checked)
            }}
            disabled={product.isRemoved}
          />
        )
      }

      columns.unshift(selectionColumn)
    }
    return columns
  }, [
    allProductIds,
    checkAll,
    checkedProductIds.byKey,
    clearCheckedProducts,
    getInfraction,
    indeterminate,
    isImperial,
    isReadOnly,
    projectIsProLicence,
    record,
    setBulkCheckedProductId,
    setCheckedProductId
  ])

  // Update select all indicator based on number selected
  useEffect(() => {
    // Filter out the Select All checkbox so it's not included in the count
    const filteredCheckedProductIds = checkedProductIds.list.filter(
      id => id.length > 1
    )
    if (filteredCheckedProductIds.length === 0) {
      setIndeterminate(false)
      setCheckAll(false)
    } else if (filteredCheckedProductIds.length < allProductIds.length) {
      setIndeterminate(true)
      setCheckAll(false)
    } else {
      setIndeterminate(false)
      setCheckAll(true)
    }
  }, [allProductIds, checkedProductIds.list])

  let assignedTo = ''

  if (record.assignedTo) {
    assignedTo = convertAssignedToArrayToString(record.assignedTo)
  }

  // Tracking product groups updated
  const EV_PRODUCT_GROUPS_UPDATED = 'frontend_records_product_groups_updated'
  const trackProductGroupsUpdated = useCallback(() => {
    dispatch(
      trackMixpanel(EV_PRODUCT_GROUPS_UPDATED, {
        project_id: projectId,
        account_id: accountId,
        record_id: record.id
      })
    )
  }, [accountId, dispatch, projectId, record.id])

  // Updating Product Groups
  const handleUpdateProductGroups = useCallback(() => {
    const onProductGroupSaveComplete = () => {
      productGroupsReload()
      setSavingProductGroups(false)
    }
    // Close and reset modal
    setShowProductGroupsModal(false)
    setSelectedProductGroupId(null)
    setSavingProductGroups(true)

    const baseRecord = _.cloneDeep(record)
    const updatedRecord = produce(baseRecord, draft => {
      draft.data.products.forEach(product => {
        if (product.isDeleted) {
          return
        }
        if (checkedProductIds.byKey[product.id]) {
          product.productGroups = !selectedProductGroupId
            ? []
            : [
                {
                  name: allProductGroups.data.find(
                    group => group.productGroupId === selectedProductGroupId
                  ).name,
                  productGroupId: selectedProductGroupId
                }
              ]
        }
      })
    })

    trackProductGroupsUpdated()

    editing.submitRecord(updatedRecord, onProductGroupSaveComplete, isImperial)
  }, [
    allProductGroups.data,
    checkedProductIds.byKey,
    editing,
    isImperial,
    productGroupsReload,
    record,
    selectedProductGroupId,
    trackProductGroupsUpdated
  ])

  return (
    <>
      <MainSection>
        <Flex.Row amount={0} verticalCentre>
          <SubHeading
            id="DeliveryDetailHeading"
            isChild
            style={{ fontSize: '1.125rem', color: colours.CORE_NAVY }}
          >
            Delivery Record
          </SubHeading>

          <Flex.By amount={1} />
          {record.canEdit && (
            <Flex.By amount={0}>
              <ViewInAdminButton
                record={record}
                accountId={accountId}
                moduleName={moduleName}
              />
              <Spacer.Tiny />
              <EditRecordButton />
              <Spacer.Tiny />
              <RemoveRecordButton />
            </Flex.By>
          )}
        </Flex.Row>

        <SubSection style={{ marginTop: '1.25rem' }}>
          <SubSectionHeading>General</SubSectionHeading>
          <DetailRow
            label="Assigned to"
            content={assignedTo}
            testId={'assigned-to'}
            valueAdd={{
              fieldName: 'assignedTo',
              ...record.dataSourceTracking?.assignedTo
            }}
          />

          <DetailRow
            label="Record created"
            infraction={getInfraction('createdAt')}
            content={
              record.createdAt
                ? new Date(record.createdAt).toLocaleString(
                    [],
                    DateFormatFull(projectTimeZone)
                  )
                : null
            }
          />
        </SubSection>

        <SubSection>
          <SubSectionHeading>Delivery</SubSectionHeading>
          <DetailRow
            label="Arrival date and time"
            infraction={getInfraction('data.deliveryArrivalDate')}
            content={
              record.data.deliveryArrivalDate
                ? new Date(record.data.deliveryArrivalDate).toLocaleString(
                    [],
                    DateFormatFull(projectTimeZone)
                  )
                : null
            }
            valueAdd={{
              fieldName: 'deliveryArrivalDate',
              ...record.dataSourceTracking?.deliveryArrivalDate
            }}
          />

          <DetailRow
            label="Exit date and time"
            infraction={getInfraction('data.deliveryExitDate')}
            content={
              record.data.deliveryExitDate
                ? new Date(record.data.deliveryExitDate).toLocaleString(
                    [],
                    DateFormatFull(projectTimeZone)
                  )
                : null
            }
            valueAdd={{
              fieldName: 'deliveryExitDate',
              ...record.dataSourceTracking?.deliveryExitDate
            }}
          />

          <DetailRow
            label="Delivery ID"
            content={record.data.deliveryId}
            infraction={getInfraction('data.deliveryId')}
            valueAdd={{
              fieldName: 'deliveryId',
              ...record.dataSourceTracking?.deliveryId
            }}
          />

          <DetailRow
            label="Site address"
            content={record.data.deliveryAddressKnown}
            infraction={getInfraction('data.deliveryAddressKnown')}
            valueAdd={{
              fieldName: 'deliveryAddressKnown',
              ...record.dataSourceTracking?.deliveryAddressKnown
            }}
          />

          <DetailRow
            label="Dispatch address"
            content={record.data.dispatchAddressKnown}
            infraction={getInfraction('data.dispatchAddressKnown')}
            valueAdd={{
              fieldName: 'dispatchAddressKnown',
              ...record.dataSourceTracking?.dispatchAddressKnown
            }}
          />

          <DetailRow
            label="Receiver reference"
            content={record.data.receiverReferenceNumber}
            infraction={getInfraction('data.receiverReferenceNumber')}
            valueAdd={{
              fieldName: 'receiverReferenceNumber',
              ...record.dataSourceTracking?.receiverReferenceNumber
            }}
          />
        </SubSection>

        <SubSection>
          <SubSectionHeading>Supplier</SubSectionHeading>

          <DetailRow
            label="Company name"
            content={record.data.knownSupplierName}
            infraction={getInfraction('data.knownSupplierName')}
            valueAdd={{
              fieldName: 'knownSupplierName',
              ...record.dataSourceTracking?.knownSupplierName
            }}
          />

          <DetailRow
            label="Reference"
            content={record.data.supplierReferenceNumber}
            infraction={getInfraction('data.supplierReferenceNumber')}
            valueAdd={{
              fieldName: 'supplierReferenceNumber',
              ...record.dataSourceTracking?.supplierReferenceNumber
            }}
          />

          <DetailRow
            label="Certifications"
            infraction={getInfraction('data.knownSupplierCertifications')}
            valueAdd={{
              fieldName: 'knownSupplierCertifications',
              ...record.dataSourceTracking?.knownSupplierCertifications
            }}
          >
            <Flex.Col>
              {record.data.knownSupplierCertifications?.map(
                ({ name, resourceURI }) => (
                  <CertificationLinkText key={name + resourceURI}>
                    {resourceURI ? (
                      <a href={resourceURI} target="_blank" rel="noreferrer">
                        {name}
                      </a>
                    ) : (
                      name
                    )}
                  </CertificationLinkText>
                )
              )}
            </Flex.Col>
          </DetailRow>

          <DetailRow
            label="Trade contractor"
            content={record.data.tradeContractor}
            infraction={getInfraction('data.tradeContractor')}
            valueAdd={{
              fieldName: 'tradeContractor',
              ...record.dataSourceTracking?.tradeContractor
            }}
          />

          <DetailRow
            label="Vehicle registration"
            content={record.data.vehicleRegistration}
            infraction={getInfraction('data.vehicleRegistration')}
            valueAdd={{
              fieldName: 'vehicleRegistration',
              ...record.dataSourceTracking?.vehicleRegistration
            }}
          />

          <DetailRow
            label="External reference"
            content={record.data.clientReference}
            infraction={getInfraction('data.clientReference')}
            valueAdd={{
              fieldName: 'clientReference',
              ...record.dataSourceTracking?.clientReference
            }}
          />

          <DetailRow
            label="Notes"
            content={record.data.notes}
            infraction={getInfraction('data.notes')}
            valueAdd={{
              fieldName: 'notes',
              ...record.dataSourceTracking?.notes
            }}
          />
        </SubSection>
        <SubSection>
          <SubSectionHeading>Journey</SubSectionHeading>
          <DetailRow
            label="Journey distance (mi)"
            content={
              record.data.journey?.distanceMiles
                ? _.round(record.data.journey.distanceMiles, 2).toString()
                : null
            }
            infraction={getInfraction('data.journey.distanceMiles')}
            tooltip="Journey distance represents the laden journey only"
            valueAdd={{
              fieldName: 'distanceMiles',
              ...record.dataSourceTracking?.journey
            }}
          />
          <DetailRow
            label={
              <span>
                Laden journey emissions (kg CO<sub>2</sub>e)
              </span>
            }
            content={
              record.data.journey?.ladenEmissionsKgCO2e
                ? _.round(
                    record.data.journey.ladenEmissionsKgCO2e,
                    2
                  ).toString()
                : null
            }
            infraction={getInfraction('data.journey.ladenEmissionsKgCO2e')}
            upgradeToPro={!projectIsProLicence}
            valueAdd={{
              fieldName: 'ladenEmissionsKgCO2e',
              ...record.dataSourceTracking?.journey
            }}
          />
          <DetailRow
            label={
              <span>
                Unladen journey emissions (kg CO<sub>2</sub>e)
              </span>
            }
            content={
              record.data.journey?.unladenEmissionsKgCO2e
                ? _.round(
                    record.data.journey.unladenEmissionsKgCO2e,
                    2
                  ).toString()
                : null
            }
            infraction={getInfraction('data.journey.unladenEmissionsKgCO2e')}
            upgradeToPro={!projectIsProLicence}
            valueAdd={{
              fieldName: 'unladenEmissionsKgCO2e',
              ...record.dataSourceTracking?.journey
            }}
          />
        </SubSection>
      </MainSection>

      <MainSection>
        <AttachmentsHeader record={record} />
        <AttachmentsSection record={record} attachments={record.attachments} />
      </MainSection>

      <MainSection>
        <Flex.Row amount={0} verticalCentre style={{ gap: '1.5rem' }}>
          <SubHeading
            isChild
            style={{ fontSize: '1.125rem', color: colours.CORE_NAVY }}
          >
            Products
          </SubHeading>

          {record.canEdit &&
            !isReadOnly &&
            record.data.products &&
            record.data.products.length > 0 && (
              <ColourButton.Basic
                onClick={setShowProductGroupsModal}
                disabled={
                  !checkedProductIds || checkedProductIds.list.length < 1
                }
              >
                <Tooltip
                  title={
                    checkedProductIds && checkedProductIds.list.length > 0
                      ? null
                      : 'Select at least 1 product to update product group'
                  }
                  placement="right"
                >
                  Update product group
                </Tooltip>
              </ColourButton.Basic>
            )}
        </Flex.Row>

        <SubSection style={{ marginTop: '1.25rem', paddingTop: '0' }}>
          <TableSection>
            <Table
              id="products-table"
              dataSource={dataForProductsTable ?? []}
              rowKey={row => row.id}
              columns={productTableHeaders}
              style={{ borderCollapse: 'separate', borderSpacing: '0 5px' }}
              pagination={false}
              locale={{ emptyText: 'No products' }}
              loading={{
                spinning: savingProductGroups,
                indicator: (
                  <div>
                    <SmallSpinner />
                  </div>
                )
              }}
              onRow={(record, index) => ({
                style: record.isRemoved
                  ? {
                      textDecoration: 'line-through',
                      color: colours.BATTLESHIP_GREY
                    }
                  : {}
              })}
            />
          </TableSection>
        </SubSection>
      </MainSection>

      <Modal
        title={<Text dim>Update product group</Text>}
        open={showProductGroupsModal}
        closeIcon={<icons.Cross colour={colours.BLACK} size={sizes.SMALL} />}
        onCancel={() => setShowProductGroupsModal(false)}
        footer={null}
        transitionName=""
        wrapClassName="common-modal"
      >
        <h2>
          Set product group for the selected product
          {checkedProductIds && checkedProductIds.list.length > 1 ? 's' : ''}
        </h2>
        <Row style={{ margin: '20px 0 10px 0' }}>
          <Col flex="auto">
            {!productGroupsLoading && !productGroupsError && (
              <SelectProductGroup
                productGroups={allProductGroups.data}
                selectedProductGroupId={selectedProductGroupId}
                setSelectedProductGroupId={setSelectedProductGroupId}
              />
            )}
          </Col>
        </Row>
        <Row style={{ margin: '20px 0 0 0' }}>
          <Col flex="auto">
            <p className="warning">
              Warning: Updating product groups will remove any estimates and
              calculated carbon.
            </p>
          </Col>
        </Row>
        <Row style={{ justifyContent: 'flex-end', marginTop: '1.5rem' }}>
          <Col>
            <ColourButton.Basic
              onClick={() => setShowProductGroupsModal(false)}
            >
              Cancel
            </ColourButton.Basic>
          </Col>
          <Spacer.Tiny />
          <Col>
            <ColourButton.Action onClick={handleUpdateProductGroups}>
              Update product
              {checkedProductIds && checkedProductIds.list.length > 1
                ? 's'
                : ''}
            </ColourButton.Action>
          </Col>
        </Row>
      </Modal>
    </>
  )
}

Delivery.propTypes = {
  record: PropTypes.object.isRequired
}

const ProductControls = ({ record, index }) => {
  const getInfraction = useInfractionGetter()

  const item = record.data.products[index]
  const itemValueAdds = record.dataSourceTracking?.products[index]

  const projectTimeZone = useSelector(
    state => state.projectDetails.modules?.timeZone
  )

  const projectCountry = useSelector(
    state => state.projectDetails.modules?.country
  )

  const isImperial = imperial(projectCountry)

  return (
    <div style={{ display: 'flex' }}>
      <LineItemDetailButton
        title={`Product Details`}
        detail={
          <ResponsiveTable.AutoCard
            headers={productDetailHeaders(projectTimeZone, isImperial)}
            index={index}
            item={item}
            itemValueAdds={itemValueAdds}
            titleKey="name"
            removed={item.isRemoved}
            renderIcon={key => {
              const infraction = getInfraction(`data.products[${index}].${key}`)
              return infraction ? <InfractionFlag /> : null
            }}
            controls={
              <LineItemButtons
                record={record}
                index={index}
                isImperial={isImperial}
              />
            }
          />
        }
      />

      <Spacer.Tiny />
      <LineItemButtons record={record} index={index} isImperial={isImperial} />
    </div>
  )
}

ProductControls.propTypes = {
  record: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired
}

const productDetailHeaders = (projectTimeZone, isImperial) => [
  { key: 'name', label: 'Given Name' },
  {
    key: 'productionTime',
    label: 'Produced at',
    render: item =>
      item.productionTime ? (
        <Text>
          {new Date(item.productionTime).toLocaleString(
            [],
            DateFormatFull(projectTimeZone)
          )}
        </Text>
      ) : null
  },
  {
    key: 'usedAtTime',
    label: 'Used at',
    render: item =>
      item.usedAtTime ? (
        <Text>
          {new Date(item.usedAtTime).toLocaleString(
            [],
            DateFormatFull(projectTimeZone)
          )}
        </Text>
      ) : null
  },
  { key: 'height', label: isImperial ? 'Height' : 'Height (m)' },
  { key: 'width', label: isImperial ? 'Width' : 'Width (m)' },
  { key: 'length', label: isImperial ? 'Length' : 'Length (m)' },
  {
    key: 'calculatedVolume',
    label: isImperial ? (
      'Calculated volume'
    ) : (
      <>
        Calculated volume (m<sup>3</sup>)
      </>
    )
  },
  {
    key: 'givenVolume',
    label: isImperial ? (
      'Volume'
    ) : (
      <>
        Volume (m<sup>3</sup>)
      </>
    )
  },
  {
    key: 'estimatedWeight',
    label: isImperial ? 'Estimated weight' : 'Estimated weight (t)'
  },
  { key: 'weight', label: isImperial ? 'Weight' : 'Weight (t)' },
  { key: 'quantity', label: 'Quantity (units)' },
  {
    key: 'givenCertifications',
    label: 'Certifications',
    render: item => {
      const certifications = (item.givenCertifications ?? []).filter(Boolean)
      if (!certifications?.length) {
        return null
      }
      return (
        <div>
          {certifications.map((certification, index) => (
            <Text
              key={index}
              style={
                index < certifications.length - 1
                  ? { marginRight: '0.25rem' }
                  : {}
              }
            >
              {certification}
            </Text>
          ))}
        </div>
      )
    }
  },
  // 20/01/2025 - Comment out knownCertifications, but we may reinstate it later
  // {
  //   key: 'knownCertifications',
  //   label: 'Known Certifications',
  //   render: item => {
  //     const certifications = item.knownCertifications ?? []
  //     if (!certifications?.length) {
  //       return null
  //     }

  //     return (
  //       <div>
  //         {certifications.map(({ name, resourceURI }, index) => (
  //           <CertificationLinkText key={index}>
  //             {resourceURI ? (
  //               <a href={resourceURI} target="_blank" rel="noreferrer">
  //                 {name}
  //               </a>
  //             ) : (
  //               name
  //             )}
  //           </CertificationLinkText>
  //         ))}
  //       </div>
  //     )
  //   }
  // },
  {
    key: 'embodiedCarbonKgCO2e',
    label: (
      <span>
        Embodied carbon (kg CO<sub>2</sub>e)
      </span>
    ),
    render: item =>
      item.embodiedCarbonKgCO2e ? _.round(item.embodiedCarbonKgCO2e, 2) : null
  }
]
const MainSection = styled.div`
  padding: 1.25rem 1.25rem 1.5rem 1.375rem;
  margin: 0 0 1.25rem -0.5rem;
  box-shadow: 0px 1px 3px 0px #00000026;
  background-color: ${colours.WHITE};
  border-radius: 10px;
`
const SubSection = styled.div`
  padding: 1rem 1.25rem 1.125rem;
  background-color: ${colours.CONTROL_PANEL_GREY};
  border-radius: 10px;
  margin-top: 0.625rem;
`
const SubSectionHeading = styled.h3`
  color: ${colours.CORE_NAVY};
  font-size: 0.9375rem;
  font-family:
    'Jakarta Sans',
    'Roboto',
    'Oxygen',
    'Ubuntu',
    'Helvetica Neue',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif !important;
  font-weight: 800;
  line-height: 1.375rem;
`
const CertificationLinkText = styled(Text)`
  margin-right: 0.25rem;
  :first-child {
    margin-top: 0;
  }
`
const StyledLabel = styled(Flex.Row)`
  gap: 4px;
  align-items: center;
`
const TableSection = styled.div`
  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td,
  .ant-table tfoot > tr > th,
  .ant-table tfoot > tr > td {
    padding: 0.5rem;
  }

  .ant-table-thead > tr > th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    span,
    sup {
      text-transform: none;
      letter-spacing: initial;
    }
  }
`
const NegativeMargin = styled.div`
  margin: 0 -0.5rem;
`
